import { NgZone } from '@angular/core';

import { ErrorReportingService } from './error-reporting.service';

let installed = false;

/**
 * Installa i listener globali `window.error` e `window.unhandledrejection` (SPEC "Mobile": niente
 * `provideBrowserGlobalErrorListeners`, che qui non è attivo). Vanno installati UNA sola volta per
 * ciclo di vita dell'app — la guardia a modulo protegge da una seconda chiamata accidentale (es. un
 * secondo bootstrap in test).
 *
 * Rientrano nella zona Angular con `NgZone.run`: arrivano da fuori change-detection (gestori nativi
 * del browser), non da un ciclo Angular. Una parte di questi errori può ESSERE GIÀ passata anche da
 * `ClientErrorHandler` (zone.js instrada spesso lì le eccezioni sincrone e alcune promise): non si
 * evita la doppia cattura con un flag ad-hoc, ma il dedup del reporter (stessa chiave client) la
 * riassorbe in un'unica riga con `ripetizioni` incrementate, invece di un doppio invio.
 */
export function installGlobalErrorListeners(ngZone: NgZone, reporter: ErrorReportingService): void {
  if (installed) {
    return;
  }
  installed = true;

  window.addEventListener('error', (event: ErrorEvent) => {
    ngZone.run(() => {
      reporter.reportJsError(event?.error ?? event?.message ?? event);
    });
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    // Con zone.js l'evento arriva sintetizzato e, con `throwOriginal` (default), senza `reason`:
    // quel caso lo copre l'aggancio a zone.js qui sotto. Un evento vuoto non va segnalato.
    if (event?.reason === undefined) {
      return;
    }
    ngZone.run(() => {
      reporter.reportJsError(event.reason);
    });
  });

  agganciaPromiseNonGestiteDiZone(ngZone, reporter);
}

type ZoneGlobale = {
  __symbol__?: (nome: string) => string;
  [chiave: string]: unknown;
};

/**
 * Promise rifiutate FUORI dalla zona Angular (callback del bridge nativo, codice di terze parti):
 * zone.js le intercetta prima del browser e, con `throwOriginal` attivo (default di zone.js 0.15),
 * inoltra ai listener `unhandledrejection` un evento con `reason: undefined` — l'errore andrebbe
 * perso. Si avvolge quindi il gestore che zone.js invoca in quel momento, che riceve l'errore
 * originale. Le rejection DENTRO la zona Angular non passano di qui: le riceve l'`ErrorHandler`.
 */
function agganciaPromiseNonGestiteDiZone(ngZone: NgZone, reporter: ErrorReportingService): void {
  try {
    const zone = (window as unknown as { Zone?: ZoneGlobale }).Zone;
    if (!zone || typeof zone.__symbol__ !== 'function') {
      return;
    }
    const simbolo = zone.__symbol__('unhandledPromiseRejectionHandler');
    const precedente = zone[simbolo];
    zone[simbolo] = function (this: unknown, e: unknown): void {
      try {
        const errore = (e as { rejection?: unknown } | null)?.rejection ?? e;
        ngZone.run(() => reporter.reportJsError(errore));
      } catch {
        // La segnalazione non deve mai interferire con zone.js.
      }
      if (typeof precedente === 'function') {
        try {
          // Con `throwOriginal` il gestore di zone.js prova a costruire un PromiseRejectionEvent
          // senza `promise` e lancia: zone.js stesso lo ignorerebbe, qui lo si contiene.
          precedente.call(this, e);
        } catch {
          // Nessun effetto sull'app.
        }
      }
    };
  } catch {
    // Ambiente senza zone.js: restano i listener window.
  }
}
